export type ScanReviewVerdict = 'likely_reliable' | 'review_recommended' | 'low_confidence';
export type ScanReviewSignalSeverity = 'success' | 'warning' | 'danger';
export type ScanReviewSignalCode =
  | 'broadCoverage'
  | 'limitedPageCoverage'
  | 'scanFailed'
  | 'zeroIssuesLimitedCoverage'
  | 'highPageFailures'
  | 'evidenceStrong'
  | 'evidenceIncomplete'
  | 'snippetIncomplete'
  | 'selectorIncomplete'
  | 'guidanceStrong'
  | 'remediationIncomplete'
  | 'developerGuidanceIncomplete'
  | 'likelyCauseIncomplete'
  | 'issuesConcentratedSinglePage'
  | 'authBlocked'
  | 'authCoverageLimited'
  | 'authRefreshFailed'
  | 'repeatedRunsIdentical';

export interface ScanReviewIssueType {
  impact: string;
  occurrenceCount?: number | null;
  affectedPages?: number | null;
  samplePageUrl?: string | null;
  samplePageEvidenceId?: string | null;
  remediationGuidance?: string | null;
  likelyCause?: string | null;
  developerFixGuidance?: string | null;
}

export interface ScanReviewIssueInstance {
  pageUrl?: string | null;
  selector?: string | null;
  htmlSnippet?: string | null;
  pageEvidence?: { id: string } | null;
}

export interface ScanReviewAuthContext {
  mode: 'public' | 'bearer_token' | 'session_storage_state' | 'form_login';
  blockedByAuthFailure: boolean;
  failureCategory?: string | null;
  refreshAttempted?: boolean;
  refreshSucceeded?: boolean;
  authenticatedPagesScanned?: number | null;
  discoveredRouteCount?: number | null;
}

export interface ScanReviewTrendEntry {
  totalIssues: number;
  pagesScanned: number;
  criticalIssues?: number;
}

export interface ScanReviewInput {
  status: string;
  pagesDiscovered?: number | null;
  pagesScanned: number;
  pagesFailed?: number | null;
  totalIssues: number;
  severityCounts: {
    critical: number;
    serious: number;
    moderate: number;
    minor: number;
  };
  issueTypes?: ScanReviewIssueType[];
  issueInstances?: ScanReviewIssueInstance[];
  auth?: ScanReviewAuthContext | null;
  trend?: ScanReviewTrendEntry[];
}

export interface ScanReviewSignal {
  code: ScanReviewSignalCode;
  severity: ScanReviewSignalSeverity;
  data?: Record<string, number | string>;
}

export interface ScanReviewMetrics {
  pagesDiscovered: number | null;
  pagesScanned: number;
  pagesFailed: number | null;
  totalIssues: number;
  issueTypes: number;
  issueInstances: number;
  uniqueIssuePages: number;
  issuePageConcentration: number | null;
  evidenceCoverage: number | null;
  domSnippetCoverage: number | null;
  selectorCoverage: number | null;
  remediationCoverage: number | null;
  developerGuidanceCoverage: number | null;
  likelyCauseCoverage: number | null;
  pageFailureRate: number | null;
  authMode: ScanReviewAuthContext['mode'];
  authBlocked: boolean;
  authenticatedPagesScanned: number | null;
  discoveredRouteCount: number | null;
  repeatedRunsIdentical: boolean;
  severityCounts: ScanReviewInput['severityCounts'];
}

export interface ScanReviewResult {
  verdict: ScanReviewVerdict;
  signals: ScanReviewSignal[];
  metrics: ScanReviewMetrics;
}

function hasText(value: string | null | undefined): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

function ratio(matched: number, total: number): number | null {
  if (total <= 0) {
    return null;
  }
  return matched / total;
}

function percent(value: number): number {
  return Math.round(value * 100);
}

function fieldCoverage<T>(
  entries: T[],
  getter: (entry: T) => string | null | undefined,
): number | null {
  if (entries.length === 0) {
    return null;
  }

  const withFieldDeclared = entries.filter((entry) => getter(entry) !== undefined);
  if (withFieldDeclared.length === 0) {
    return null;
  }

  return ratio(
    withFieldDeclared.filter((entry) => hasText(getter(entry))).length,
    withFieldDeclared.length,
  );
}

function sortSignals(signals: ScanReviewSignal[]): ScanReviewSignal[] {
  const severityOrder: Record<ScanReviewSignalSeverity, number> = {
    danger: 0,
    warning: 1,
    success: 2,
  };

  return [...signals].sort((left, right) => severityOrder[left.severity] - severityOrder[right.severity]);
}

export function deriveScanReview(input: ScanReviewInput): ScanReviewResult {
  const issueTypes = input.issueTypes ?? [];
  const issueInstances = input.issueInstances ?? [];
  const pagesFailed = input.pagesFailed ?? null;
  const pagesDiscovered = input.pagesDiscovered ?? null;
  const issueInstanceCount = issueInstances.length;

  const uniqueIssuePages = new Set(
    issueInstances
      .map((instance) => instance.pageUrl?.trim())
      .filter((value): value is string => Boolean(value)),
  );

  const pageCounts = new Map<string, number>();
  for (const instance of issueInstances) {
    const pageUrl = instance.pageUrl?.trim();
    if (!pageUrl) {
      continue;
    }
    pageCounts.set(pageUrl, (pageCounts.get(pageUrl) ?? 0) + 1);
  }

  const maxPerPage = pageCounts.size > 0 ? Math.max(...pageCounts.values()) : 0;

  const evidenceCoverage =
    issueInstanceCount > 0
      ? ratio(
          issueInstances.filter((instance) => Boolean(instance.pageEvidence?.id)).length,
          issueInstanceCount,
        )
      : ratio(
          issueTypes.filter((issue) => hasText(issue.samplePageEvidenceId)).length,
          issueTypes.length,
        );

  const domSnippetCoverage =
    issueInstanceCount > 0
      ? ratio(issueInstances.filter((instance) => hasText(instance.htmlSnippet)).length, issueInstanceCount)
      : null;

  const selectorCoverage =
    issueInstanceCount > 0
      ? ratio(issueInstances.filter((instance) => hasText(instance.selector)).length, issueInstanceCount)
      : null;

  const remediationCoverage = fieldCoverage(issueTypes, (issue) => issue.remediationGuidance);
  const developerGuidanceCoverage = fieldCoverage(issueTypes, (issue) => issue.developerFixGuidance);
  const likelyCauseCoverage = fieldCoverage(issueTypes, (issue) => issue.likelyCause);

  const pageFailureRate =
    pagesDiscovered && pagesDiscovered > 0 && pagesFailed !== null
      ? pagesFailed / pagesDiscovered
      : null;

  const authMode = input.auth?.mode ?? 'public';
  const authBlocked = Boolean(
    input.auth && (input.auth.blockedByAuthFailure || input.auth.failureCategory),
  );
  const authenticatedPagesScanned = input.auth?.authenticatedPagesScanned ?? null;
  const discoveredRouteCount = input.auth?.discoveredRouteCount ?? null;

  const recentTrend = (input.trend ?? []).slice(-3);
  const repeatedRunsIdentical =
    recentTrend.length >= 3 &&
    recentTrend.every(
      (entry) =>
        entry.totalIssues === recentTrend[0]!.totalIssues &&
        entry.pagesScanned === recentTrend[0]!.pagesScanned &&
        (entry.criticalIssues ?? 0) === (recentTrend[0]!.criticalIssues ?? 0),
    );

  const metrics: ScanReviewMetrics = {
    pagesDiscovered,
    pagesScanned: input.pagesScanned,
    pagesFailed,
    totalIssues: input.totalIssues,
    issueTypes: issueTypes.length,
    issueInstances: issueInstanceCount,
    uniqueIssuePages: uniqueIssuePages.size,
    issuePageConcentration: issueInstanceCount > 0 ? maxPerPage / issueInstanceCount : null,
    evidenceCoverage,
    domSnippetCoverage,
    selectorCoverage,
    remediationCoverage,
    developerGuidanceCoverage,
    likelyCauseCoverage,
    pageFailureRate,
    authMode,
    authBlocked,
    authenticatedPagesScanned,
    discoveredRouteCount,
    repeatedRunsIdentical,
    severityCounts: input.severityCounts,
  };

  const signals: ScanReviewSignal[] = [];

  if (input.status === 'failed' || input.status === 'auth_failed') {
    signals.push({
      code: 'scanFailed',
      severity: 'danger',
      data: { status: input.status },
    });
  }

  if (input.pagesScanned <= 2) {
    signals.push({
      code: 'limitedPageCoverage',
      severity: input.pagesScanned === 0 ? 'danger' : 'warning',
      data: { pages: input.pagesScanned },
    });
  } else if (input.pagesScanned >= 5 && (pageFailureRate === null || pageFailureRate <= 0.15)) {
    signals.push({
      code: 'broadCoverage',
      severity: 'success',
      data: { pages: input.pagesScanned },
    });
  }

  if (input.totalIssues === 0 && input.pagesScanned <= 2) {
    signals.push({
      code: 'zeroIssuesLimitedCoverage',
      severity: 'warning',
      data: { pages: input.pagesScanned },
    });
  }

  if (pageFailureRate !== null && pagesFailed !== null && pagesDiscovered !== null) {
    if (pageFailureRate >= 0.5) {
      signals.push({
        code: 'highPageFailures',
        severity: 'danger',
        data: { failed: pagesFailed, total: pagesDiscovered },
      });
    } else if (pageFailureRate >= 0.25) {
      signals.push({
        code: 'highPageFailures',
        severity: 'warning',
        data: { failed: pagesFailed, total: pagesDiscovered },
      });
    }
  }

  if (input.totalIssues > 0) {
    if (evidenceCoverage !== null) {
      if (evidenceCoverage >= 0.7) {
        signals.push({
          code: 'evidenceStrong',
          severity: 'success',
          data: { coverage: percent(evidenceCoverage) },
        });
      } else if (evidenceCoverage < 0.35) {
        signals.push({
          code: 'evidenceIncomplete',
          severity: 'danger',
          data: { coverage: percent(evidenceCoverage) },
        });
      } else if (evidenceCoverage < 0.7) {
        signals.push({
          code: 'evidenceIncomplete',
          severity: 'warning',
          data: { coverage: percent(evidenceCoverage) },
        });
      }
    }

    if (domSnippetCoverage !== null && domSnippetCoverage < 0.45) {
      signals.push({
        code: 'snippetIncomplete',
        severity: 'warning',
        data: { coverage: percent(domSnippetCoverage) },
      });
    }

    if (selectorCoverage !== null && selectorCoverage < 0.45) {
      signals.push({
        code: 'selectorIncomplete',
        severity: 'warning',
        data: { coverage: percent(selectorCoverage) },
      });
    }

    const guidanceCoverages = [
      remediationCoverage,
      developerGuidanceCoverage,
      likelyCauseCoverage,
    ].filter((value): value is number => value !== null);

    if (guidanceCoverages.length > 0 && guidanceCoverages.every((value) => value >= 0.75)) {
      signals.push({ code: 'guidanceStrong', severity: 'success' });
    }

    if (remediationCoverage !== null && remediationCoverage < 0.6) {
      signals.push({
        code: 'remediationIncomplete',
        severity: 'warning',
        data: { coverage: percent(remediationCoverage) },
      });
    }

    if (developerGuidanceCoverage !== null && developerGuidanceCoverage < 0.6) {
      signals.push({
        code: 'developerGuidanceIncomplete',
        severity: 'warning',
        data: { coverage: percent(developerGuidanceCoverage) },
      });
    }

    if (likelyCauseCoverage !== null && likelyCauseCoverage < 0.6) {
      signals.push({
        code: 'likelyCauseIncomplete',
        severity: 'warning',
        data: { coverage: percent(likelyCauseCoverage) },
      });
    }
  }

  if (
    metrics.issuePageConcentration !== null &&
    metrics.issuePageConcentration >= 0.85 &&
    metrics.uniqueIssuePages <= 1 &&
    input.pagesScanned >= 4
  ) {
    signals.push({
      code: 'issuesConcentratedSinglePage',
      severity: 'warning',
      data: { share: percent(metrics.issuePageConcentration) },
    });
  }

  if (authMode !== 'public') {
    if (authBlocked) {
      signals.push({
        code: 'authBlocked',
        severity: 'danger',
        data: { mode: authMode },
      });
    } else if ((authenticatedPagesScanned ?? 0) <= 1 || (discoveredRouteCount ?? 0) <= 1) {
      signals.push({
        code: 'authCoverageLimited',
        severity: 'warning',
        data: {
          pages: authenticatedPagesScanned ?? 0,
          routes: discoveredRouteCount ?? 0,
        },
      });
    }

    if (input.auth?.refreshAttempted && !input.auth.refreshSucceeded) {
      signals.push({
        code: 'authRefreshFailed',
        severity: 'warning',
      });
    }
  }

  if (repeatedRunsIdentical) {
    signals.push({
      code: 'repeatedRunsIdentical',
      severity: 'warning',
    });
  }

  const verdict: ScanReviewVerdict = signals.some((signal) => signal.severity === 'danger')
    ? 'low_confidence'
    : signals.some((signal) => signal.severity === 'warning')
      ? 'review_recommended'
      : 'likely_reliable';

  return {
    verdict,
    signals: sortSignals(signals),
    metrics,
  };
}
